import type { WishlistAll, WishlistStatus, WishlistStatusMessage } from "~/stores/types/wishlist";
import type { BasicProductDetails } from "~/stores/types/product";
import { defineStore, useUserStore, useRuntimeConfig } from '#imports';

const API_BASE = '/wp-json/content-search/v1/search?type=wishlist';

export const useProductStore = defineStore('product', {
  state: () => ({
    loadingProductBasicDetails: {} as BasicProductDetails,
    priceMapping: {},    // productId: {  currencyIsoString: priceInFloat }
    wishlists: [] as WishlistAll[],       // { id, name, productIds: number[], items: productData[] }
    wishlistLoaded: false,
  }),
  getters: {
    getBasicProductDetails(slug: string) {
      const product = this.loadingProductBasicDetails

      if (product?.productId) { // && product.slug == slug
        return product
      }

      return null
    }
  },
  actions: {
    storeBasicProductDetails (product: BasicProductDetails) {
      this.loadingProductBasicDetails = product
    },
    removeBasicProductDetails () {
      this.loadingProductBasicDetails = {}
    },
    async getWishlists (refresh = false) {
      if (!this.wishlistLoaded || refresh) {
        const wishlists = await this.fetchData(`${API_BASE}&action=list-all`);

        if (wishlists) {
          this.wishlists = wishlists;
          this.wishlistLoaded = true;
        }
      }
    },
    async getWishlistDetails (wishlistId: number, refresh: boolean = false): Promise<void> {
      const index = this.wishlists.findIndex((wishlist) => wishlist.id === wishlistId);

      if (refresh || !this.wishlists[index].items) {
        const data = await this.fetchData(`${API_BASE}&action=list-detail&wishlist-id=${wishlistId}`);

        this.wishlists[index].count = data.count;
        this.wishlists[index].items = data.items;
      }
    },
    async getWishlistOptions (productId): Promise<WishlistStatus[]> {
      return this.fetchData(`${API_BASE}&action=list-options&product-id=${productId}`);
    },
    async createWishlist (listName: string, productId: number|string|undefined) {
      return new Promise(async (resolve, reject) => {
        const productIdParam = productId ? `&product-id=${productId}` : '';
        this.fetchData(`${API_BASE}&action=list-create&list-name=${listName}${productIdParam}`)
          .then((data) => {
            this.wishlists.push(data);
            resolve(data);
          }).catch((e) => {
            reject(e);
          });
      });
    },
    async deleteWishlist (wishlistId: string) {
      return new Promise(async (resolve, reject) => {
        this.fetchData(`${API_BASE}&action=list-delete&wishlist-id=${wishlistId}`)
          .then((data) => {
            this.getWishlists(true)
            resolve(data);
          }).catch((e) => {
            reject(e);
          });
      });
    },
    async changeWishlistStatus (productId: string|number, wishlistId: string|number, addItem: boolean): Promise<WishlistStatusMessage> {
      const action = addItem
        ? 'add'
        : 'remove';
      const params = [
        `action=${action}`,
        `product-id=${productId}`,
        `wishlist-id=${wishlistId}`,
      ];
      return new Promise(async (resolve, reject) => {
        this.fetchData(`${API_BASE}&${params.join('&')}`, 'PUT')
          .then((data) => {
            // Update local wishlists array immediately after successful API call
            const wishlistIndex = this.wishlists.findIndex(wishlist => wishlist.id === wishlistId);
            const wishlist = this.wishlists[wishlistIndex];
            if (wishlistIndex !== -1 && wishlist?.productIds) {
              const productIdNum = Number(productId);
              if (addItem) {
                // Add product to wishlist if not already present
                if (!wishlist.productIds.includes(productIdNum)) {
                  wishlist.productIds.push(productIdNum);
                }
              } else {
                // Remove product from wishlist
                const productIndex = wishlist.productIds.indexOf(productIdNum);
                if (productIndex !== -1) {
                  wishlist.productIds.splice(productIndex, 1);
                }
              }
            }
            resolve(data);
          }).catch((e) => {
            reject(e);
          });
      });
    },
    async fetchData (uri: string, method: string = 'GET', body: Object = null) {
      return new Promise(async (resolve, reject) => {
        const userStore = useUserStore();

        if (!userStore.user) {
          let retries = 0;

          function pause(pauseTime) {
            return new Promise(resolve => setTimeout(() => {
              resolve();
            }, pauseTime));
          }

          // ugly solution to ensure that user data has been loaded after login
          while (!userStore.user && retries <= 10) {
            await pause(200);
            retries += 1;
          }
        }

        try {
          if (!userStore.user?.nonce) {
            resolve(null);
            return;
          }

          const init = { method };

          if (body) {
            init.body = JSON.stringify(body);
          }

          const config = useRuntimeConfig();
          const response = await fetch(`${config.public.baseUrl}${uri}&_wpnonce=${userStore.user?.nonce}`, init);
          const data = await response.json();

          if (data.status === 'ERROR' || data.data?.status >= 400) {
            reject(data);
          } else {
            resolve(data);
          }
        } catch (e) {
          reject(e);
        }
      });
    },
  },
});
